import React, {forwardRef} from 'react'
import {Radio as ChakraRadio, RadioGroup, RadioGroupProps, FormControl, FormErrorMessage, Text} from '@chakra-ui/react'

interface CustomRadioProps extends Omit<RadioGroupProps, 'children'> {
    options?: {label: string; value: string}[]
    errorMessage?: string
    children?: React.ReactNode
    defaultValue?: string
    size?: 'xs' | 'sm' | 'md' | (string & {})
}

/**
 * @name Radio
 *
 * @component
 *
 * @description
 * Custom radio button component that uses Chakra UI's `FormControl`, `RadioGroup`, and `Radio` components.
 * It supports custom options, error messages, and disabled state.
 *
 * @param {CustomRadioProps} props - The props for the Radio component.
 * @param {Array<{ value: string, label: string }>} props.options - The options for the radio buttons.
 * @param {string} [props.errorMessage] - The error message to display if the radio group is invalid.
 * @param {React.ReactNode} props.children - The children to render inside the radio button if no options are provided.
 * @param {string} props.name - The name of the radio group.
 * @param {boolean} [props.isDisabled] - Whether the radio group is disabled.
 * @param {string} [props.defaultValue] - The default value for the radio group.
 * @param {React.Ref<HTMLDivElement>} ref - The ref to forward to the `FormControl` component.
 *
 * @returns {JSX.Element} The rendered Radio component.
 */
const Radio = forwardRef<HTMLDivElement, CustomRadioProps>(({options, errorMessage, children, name, isDisabled, defaultValue, ...props}, ref) => {
    return (
        <FormControl ref={ref} id={name} isInvalid={!!errorMessage} isDisabled={isDisabled} data-testid="radio">
            <RadioGroup ref={ref} defaultValue={defaultValue} {...props}>
                {options ? (
                    options?.map((option) => (
                        <ChakraRadio key={option.value} value={option.value}>
                            <Text as="span" className="radio__label">
                                {option.label}
                            </Text>
                        </ChakraRadio>
                    ))
                ) : (
                    <ChakraRadio value="1">{children}</ChakraRadio>
                )}
            </RadioGroup>
            {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
        </FormControl>
    )
})

Radio.displayName = 'Radio'

export {Radio}
